// #2220 — landing page for a CLIENT-role login when the account holds
// ACTIVE stakeholder access on more than one project. No unique constraint
// exists on project_stakeholders(userId, projectId) — a client can hold
// several rows at once (confirmed at recon), which the pre-existing
// GET /client/my-project endpoint papered over with an arbitrary
// .limit(1) pick. This page is reached from LoginPage.tsx's CLIENT branch
// when GET /client/my-projects returns more than one project, and from
// ClientDashboard's "Switch project" link for an already-signed-in
// multi-project client.
//
// Deliberately NOT built on TenantSwitcher.tsx's mechanism: that component
// reissues a tenant-scoped JWT via POST /auth/switch-tenant and reloads the
// page, because tenant is a JWT-encoded scope for that user class. Project
// is not — a CLIENT-only user's JWT carries no tenantId at all
// (resolveNonClientDefaultTenantId excludes CLIENT memberships by design,
// per #1608), and project-scoped routes resolve tenant per-request from the
// :id in the URL (tenant-auth.middleware.ts's requireTenantMember). So
// switching projects here is a plain client-side navigation carrying
// ?projectId=, not a session-level operation — no new backend endpoint
// beyond the plural list needed.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface MyProjectSummary {
  projectId: string;
  projectName: string;
  domain: string | null;
  stakeholderId: string;
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'empty' }
  | { kind: 'ready'; projects: MyProjectSummary[] };

export default function ClientProjectPicker() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ projects: MyProjectSummary[] }>('/client/my-projects')
      .then(({ projects }) => {
        if (cancelled) return;
        if (projects.length === 0) {
          setState({ kind: 'empty' });
        } else if (projects.length === 1) {
          // Single project: skip the picker UI, same landing a
          // single-project client always got — just made explicit via
          // ?projectId= instead of relying on /client/my-project's
          // undifferentiated .limit(1).
          navigate(`/client/dashboard?projectId=${projects[0].projectId}`, { replace: true });
        } else {
          setState({ kind: 'ready', projects });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load your projects' });
      });
    return () => { cancelled = true; };
  }, [navigate]);

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh', padding: 24, background: 'var(--surface-alt)',
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 560, margin: '60px auto', padding: 40,
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 16,
  };

  if (state.kind === 'loading') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <p style={{ margin: 0, fontSize: 16, textAlign: 'center' }}>Loading your projects…</p>
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, textAlign: 'center' }}>
            Unable to load your projects
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center' }}>
            {state.message}
          </p>
        </div>
      </div>
    );
  }

  if (state.kind === 'empty') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, textAlign: 'center' }}>
            No project assigned
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center' }}>
            You don&rsquo;t currently have access to any project. If a delivery partner runs your
            workspace, your project appears here as soon as they create it. Otherwise, contact whoever
            invited you if you believe this is a mistake.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>Choose a project</h2>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--text-secondary)' }}>
          You have access to more than one project.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {state.projects.map(p => (
            <button
              key={p.projectId}
              type="button"
              data-testid={`client-project-option-${p.projectId}`}
              onClick={() => navigate(`/client/dashboard?projectId=${p.projectId}`)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                width: '100%', textAlign: 'left', padding: '14px 16px',
                borderRadius: 10, border: '1px solid var(--border)',
                background: 'var(--surface-alt)', cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 600 }}>{p.projectName}</span>
              {p.domain && (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {p.domain}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
